// src/features/records/components/shared/RecordAttachments.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styles from './RecordAttachments.module.css';
import AttachmentThumb from './AttachmentThumb';

/**
 * RecordAttachments - Displays attachments in either compact or grid layout
 * This is a presentational component that arranges AttachmentThumb components.
 * URL resolution and loading is handled by AttachmentThumb internally.
 */
const RecordAttachments = ({ 
  attachments, 
  variant = 'compact',
  onMediaClick,
  getMediaId
}) => {
  const { t } = useTranslation();
  // Handle missing or empty attachments
  if (!attachments || attachments.length === 0) {
    return null;
  }

  const isMediaAttachment = (attachment) =>
    attachment?.type === 'image' || attachment?.type === 'video';

  const getAttachmentTypeLabel = (type) => {
    const normalizedType = typeof type === 'string' ? type.trim().toLowerCase() : '';
    switch (normalizedType) {
      case 'image':
        return t('records.detail.attachments.types.image', 'image');
      case 'video':
        return t('records.detail.attachments.types.video', 'video');
      case 'audio':
        return t('records.detail.attachments.types.audio', 'audio');
      case 'document':
        return t('records.detail.attachments.types.document', 'document');
      case 'file':
        return t('records.detail.attachments.types.file', 'file');
      default:
        return normalizedType || t('records.detail.attachments.file_type_default', 'file');
    }
  };

  const resolveMediaId = (attachment, index) => {
    if (getMediaId) return getMediaId(attachment, index);
    if (attachment?.mediaId) return attachment.mediaId;
    return attachment?.id || attachment?.storagePath || String(index);
  };

  const getMediaLabel = (attachment) => {
    const name = attachment?.metadata?.filename
      || attachment?.name
      || t('records.detail.attachments.fallback_name', 'attachment');
    if (attachment?.type === 'video') {
      return t('records.detail.attachments.open_video', {
        name,
        defaultValue: 'Open video {{name}}'
      });
    }
    if (attachment?.type === 'image') {
      return t('records.detail.attachments.open_image', {
        name,
        defaultValue: 'Open image {{name}}'
      });
    }
    return t('records.detail.attachments.open_attachment', {
      name,
      defaultValue: 'Open attachment {{name}}'
    });
  };

  const handleMediaClick = (attachment, index, event) => {
    if (!onMediaClick) return;
    onMediaClick(attachment, { index, event });
  };

  // Grid variant - for RecordDetail view
  if (variant === 'grid') {
    return (
      <>
        {attachments.map((attachment, index) => {
          const isMedia = isMediaAttachment(attachment);
          const mediaId = isMedia ? resolveMediaId(attachment, index) : null;
          const className = `${styles.attachmentItem} ${attachment.type === 'video' ? styles.video : ''}`;

          if (isMedia && onMediaClick) {
            return (
              <button
                key={attachment.id || index}
                className={`${className} ${styles.attachmentButton}`}
                type="button"
                onClick={(event) => handleMediaClick(attachment, index, event)}
                data-media-id={mediaId || undefined}
                aria-label={getMediaLabel(attachment)}
              >
                <AttachmentThumb attachment={attachment} variant="grid" />
              </button>
            );
          }

          return (
            <div 
              key={attachment.id || index} 
              className={className}
              data-media-id={isMedia ? mediaId || undefined : undefined}
            >
              {isMedia ? (
                <AttachmentThumb attachment={attachment} variant="grid" />
              ) : (
                <div className={styles.filePlaceholder}>
                  <span className={styles.fileType}>
                    {getAttachmentTypeLabel(attachment.type)}
                  </span>
                  {attachment.metadata?.filename && (
                    <span className={styles.fileName}>{attachment.metadata.filename}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  }

  // Compact variant - for RecordCard view (default)
  const displayAttachments = attachments.slice(0, 3);
  const remainingCount = attachments.length - 3;

  return (
    <div className={styles.recordAttachments}>
      {displayAttachments.map((attachment, index) => (
        <div 
          key={attachment.id || index} 
          className={styles.attachmentPreview}
        >
          {attachment.type === 'image' || attachment.type === 'video' ? (
            <AttachmentThumb attachment={attachment} variant="compact" />
          ) : (
            <div className={styles.attachmentPlaceholder}>
              <span>
                {getAttachmentTypeLabel(attachment.type)}
              </span>
            </div>
          )}
        </div>
      ))}
      {remainingCount > 0 && (
        <div className={styles.attachmentMore}>
          {t('records.detail.attachments.more_count', {
            count: remainingCount,
            defaultValue: '+{{count}}'
          })}
        </div>
      )}
    </div>
  );
};

RecordAttachments.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    storagePath: PropTypes.string,
    thumbnails: PropTypes.object,
    metadata: PropTypes.shape({
      filename: PropTypes.string
    })
  })),
  variant: PropTypes.oneOf(['compact', 'grid']),
  onMediaClick: PropTypes.func,
  getMediaId: PropTypes.func
};

export default RecordAttachments;
